import { canonicaCategoria } from "@/lib/categorie";
import { EDITORIALI_PATH, firmaDi } from "@/lib/constants";
import { EDITORIALI_DEMO, editorialeDemoHref, tempoEditoriale, type EditorialeDemo } from "@/lib/editoriali";
import { FOTO_ESEMPIO } from "@/lib/editoriali-foto-esempio";
import type { TipoDerivato } from "@/lib/types";

/**
 * Il modello della raccolta «Editoriali» (app/editoriali): lo stato che sta nell'indirizzo, come si
 * riscrive in un link, la forma di una scheda e il ramo dimostrativo.
 *
 * Sta in `lib/` e senza direttiva per la stessa ragione di lib/rullo.ts:
 * lo leggono il Server Component della rotta e il modulo di ricerca, che
 * è «use client», e una funzione esportata da un modulo client e letta
 * dal server è il riferimento al modulo, non la funzione. Qui non si
 * importa `lib/nd-client` (porta il token): la domanda a monte sta in
 * lib/editoriali-dati.ts.
 */

/** Dove vive la raccolta (la costante sta in lib/constants.ts, il
 *  perché è accanto a lei). */
export { EDITORIALI_PATH };

/** L'ancora della sezione delle schede: ogni cambio di pagina ci porta
 *  il lettore, invece di rimandarlo in cima al sito. */
export const ANCORA_SCHEDE = "prospettive";

/** Sei schede per pagina: due righe della griglia a tre colonne del
 *  riferimento (uploads/Consegna_Editoriali_revisionati_OI.zip). Il banner
 *  dei servizi che si apre dopo la terza non conta fra le sei. */
export const SCHEDE_PER_PAGINA = 6;

/** Dopo quante schede si apre il banner dei servizi: una riga intera a
 *  tre colonne. Con meno schede il banner non si rende, per non lasciare
 *  una pubblicità orfana sotto un risultato solo. */
export const BANNER_DOPO = 3;

export type StatoRaccolta = { q: string; cat: string; page: number };

type SP = { [key: string]: string | string[] | undefined };

function first(v: string | string[] | undefined): string {
  return (Array.isArray(v) ? v[0] : v)?.trim() ?? "";
}

export function leggiStatoRaccolta(sp: SP): StatoRaccolta {
  const n = parseInt(first(sp.page) || "1", 10);
  return { q: first(sp.q), cat: canonicaCategoria(first(sp.cat)), page: Number.isFinite(n) && n > 0 ? n : 1 };
}

/** L'indirizzo di uno stato con qualche campo cambiato. Come
 *  `hrefRullo`: ogni cambio che non sia la sola pagina riporta a pagina
 *  1, e `page=1` non si scrive, così ogni vista ha un indirizzo solo.
 *  Niente ancora `#prospettive`: rovinerebbe il ritorno da un articolo
 *  (components/editoriali/AllaSezione.tsx). */
export function hrefRaccolta(s: StatoRaccolta, cambi: Partial<StatoRaccolta> = {}): string {
  const soloPagina = Object.keys(cambi).every((k) => k === "page");
  const n: StatoRaccolta = { ...s, ...cambi, page: soloPagina ? cambi.page ?? s.page : 1 };
  const usp = new URLSearchParams();
  if (n.q) usp.set("q", n.q);
  if (n.cat) usp.set("cat", n.cat);
  if (n.page > 1) usp.set("page", String(n.page));
  const qs = usp.toString();
  return qs ? `${EDITORIALI_PATH}?${qs}` : EDITORIALI_PATH;
}

export const conFiltro = (s: StatoRaccolta) => s.q !== "" || s.cat !== "";

/** Un editoriale nella forma che la pagina rende: il principale e le
 *  schede sono la stessa cosa, cambia solo quanto grande si disegna. */
export type SchedaEditoriale = {
  id: string;
  href: string;
  titolo: string;
  sommario: string;
  /** Vuota vuol dire assente: a monte `category` è la stringa vuota
   *  sulla quasi totalità delle righe, e chi la rende salta il pezzo. */
  categoria: string;
  firma: string;
  /** ISO, per `<time dateTime>`. */
  iso: string;
  /** «13 settembre 2026». */
  data: string;
  /** «20», il giorno in cifra grande della testata del principale. */
  giorno: string;
  /** «Settembre 2026». */
  meseAnno: string;
  foto: string | null;
  /** La didascalia della foto, quando va detta: oggi solo «Immagine di
   *  esempio» sulle foto segnaposto dell'anteprima
   *  (lib/editoriali-foto-esempio.ts). Nel ramo a monte è sempre `null`. */
  didascalia: string | null;
  /** «La controdomanda». Facoltativa: senza, la fascia non si rende. */
  controdomanda: string | null;
  /** Il pezzo è un provvisorio di riempimento? Decide la nota «Domanda
   *  dimostrativa» sotto la controdomanda, che va per pezzo e non per
   *  pagina: nel ramo dimostrativo c'è anche «E intanto si vende», che è
   *  vero e porta una domanda vera. */
  provvisorio: boolean;
  /** Notizia / Caso / Report, dal numero di voci di cronologia a monte.
   *  `null` sui provvisori (non hanno cronologia) e quando il conteggio
   *  non è disponibile: chi rende salta il pezzo e il suo separatore. */
  tipoDerivato: TipoDerivato | null;
  /** Le prime frasi intere del testo, solo sui pezzi aperti del CMS
   *  (`attaccoDelTesto`): il riquadro grande dei riservati le mostra al
   *  posto del sommario, quante ne stanno. Assente altrove. */
  attacco?: string[];
};

/** Il fuso delle date: Roma, calcolate sul server, quindi l'HTML dei due
 *  lati coincide e non passano da `useTick`. */
const FUSO = "Europe/Rome";
const fmtData = new Intl.DateTimeFormat("it-IT", { timeZone: FUSO, day: "numeric", month: "long", year: "numeric" });
const fmtGiorno = new Intl.DateTimeFormat("it-IT", { timeZone: FUSO, day: "numeric" });
const fmtMeseAnno = new Intl.DateTimeFormat("it-IT", { timeZone: FUSO, month: "long", year: "numeric" });

export function date(ms: number): Pick<SchedaEditoriale, "iso" | "data" | "giorno" | "meseAnno"> {
  const t = new Date(ms);
  const ma = fmtMeseAnno.format(t);
  return {
    iso: t.toISOString(),
    data: fmtData.format(t),
    giorno: fmtGiorno.format(t),
    meseAnno: ma.charAt(0).toUpperCase() + ma.slice(1),
  };
}

/** Un testo facoltativo ripulito: `null` e mai stringa vuota, così chi
 *  rende ha un caso solo da controllare. */
export const pieno = (v: string | null | undefined) => (v?.trim() ? v.trim() : null);

/* ─────────────────────── il ramo dimostrativo ─────────────────────── */

/** Esportata perché la legge anche la pagina di lettura dei provvisori
 *  (app/editoriali/[id]/[slug]), che in fondo mostra «Altri editoriali»
 *  con le stesse schede dell'indice. */
export function schedaDemo(e: EditorialeDemo, adesso: number): SchedaEditoriale {
  const esempio = FOTO_ESEMPIO[e.id] ?? null;
  return {
    id: e.id,
    href: editorialeDemoHref(e),
    titolo: e.titolo,
    sommario: e.sommario,
    categoria: canonicaCategoria(e.categoria),
    firma: firmaDi(null),
    ...date(tempoEditoriale(e, adesso)),
    /* Il campo vero del pezzo vince; la foto d'esempio riempie solo un
       riquadro che altrimenti sarebbe la copertina tipografica, e lo dice
       nella didascalia. */
    foto: pieno(e.foto) ?? pieno(e.copertina?.src) ?? esempio?.src ?? null,
    /* Dopo la foto, l'illustrazione d'apertura del pezzo: la card deve
       mostrare la stessa immagine che si trova aprendolo (richiesta del
       26/10/2026: «E intanto si vende» usciva in card con la foto
       d'esempio dell'industria e dentro con l'illustrazione approvata).
       Con un'immagine vera la didascalia «di esempio» non c'è. */
    didascalia: pieno(e.foto) || pieno(e.copertina?.src) ? null : esempio?.didascalia ?? null,
    controdomanda: pieno(e.controdomanda),
    provvisorio: e.provvisorio !== false,
    tipoDerivato: null,
  };
}

/** La raccolta sui provvisori di lib/editoriali.ts, con la stessa
 *  interfaccia della domanda a monte: il principale è il più recente per
 *  data (mai per posizione nell'array), le schede sono tutti gli altri,
 *  filtrati e tagliati a pagine qui perché a monte non passano.
 *
 *  La ricerca guarda titolo, sommario e categoria, cioè gli stessi campi
 *  diretti che `search` tocca a monte; ogni parola deve comparire, come
 *  fa `listCases` con i token in `$and`. */
export function raccoltaDemo(stato: StatoRaccolta, adesso: number) {
  const tutte = EDITORIALI_DEMO.map((e) => schedaDemo(e, adesso)).sort((a, b) =>
    b.iso.localeCompare(a.iso)
  );
  const principale = tutte[0] ?? null;
  const parole = stato.q.toLocaleLowerCase("it").split(/\s+/).filter(Boolean);
  const cat = stato.cat.toLocaleLowerCase("it");
  const trovate = tutte.slice(1).filter((s) => {
    if (cat && s.categoria.toLocaleLowerCase("it") !== cat) return false;
    const testo = `${s.titolo} ${s.sommario} ${s.categoria}`.toLocaleLowerCase("it");
    return parole.every((p) => testo.includes(p));
  });
  const inizio = (stato.page - 1) * SCHEDE_PER_PAGINA;
  return {
    principale,
    schede: trovate.slice(inizio, inizio + SCHEDE_PER_PAGINA),
    totale: trovate.length,
    categorie: contaCategorie(tutte.map((s) => s.categoria)),
  };
}

/** Le categorie con il loro peso, in ordine di peso e poi di nome. Una
 *  voce senza nome si salta: non si può né scrivere né scegliere. */
export function contaCategorie(nomi: string[]): { name: string; count: number }[] {
  const m = new Map<string, number>();
  for (const n of nomi) if (n.trim()) m.set(n.trim(), (m.get(n.trim()) ?? 0) + 1);
  return [...m.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, "it"));
}

export const it = (n: number) => n.toLocaleString("it-IT");
